import { Card } from 'components/Card';
import { CardGeneric } from 'components/CardGeneric';
import { CardRow } from 'components/CardRow';
import { ICONS } from 'components/Icon';
import {
  ModalScreenProps,
  ModalScreens,
  ScreenProps,
  Screens,
  Stacks,
} from 'components/Navigation/types';
import { Page } from 'components/Page';
import { Txt } from 'components/Txt';
import { useScreenSize } from 'hooks/useScreenSize';
import { useSelector } from 'hooks/useSelector';
import { theme } from 'lib/theme';
import React from 'react';
import { useDispatch } from 'react-redux';
import {
  AddCustomAction,
  AddTemplateAction,
} from 'screens/AssessmentScreen/Explorer/ExplorerActionRow';
import {
  AddCustomControl,
  AddTemplateControl,
} from 'screens/AssessmentScreen/Explorer/ExplorerControlRow';
import {
  doAseDeleteQuestionResult,
  doAseUpdateQuestion,
} from 'store/assessments/actions';
import {
  selectAseQuestionPreviousAnswer,
  selectAseTplChecklistItemsForQuestion,
  selectAseQuestionActionIds,
  selectAseQuestionControlIds,
  selectAseResultQuestion,
  selectAseTplQuestionAnswers,
  selectAseTplQuestion,
  selectAseFieldInstanceAse,
  selectAseFieldInstanceChildInstanceIsInParentAstAse,
  selectAseQuestionComment,
  selectAseResultQuestionAnswerId,
  selectAseResultChecklistItemsForQuestion,
  selectAseTemplateSettings,
  selectAseShowSuggestionsUnderChecklistItems,
  selectAseSuggestedActionsForChecklistItemResults,
  selectAseSuggestedActionsForAnswerId,
  selectAseSuggestedControlsForQuestionId,
  selectAseSuggestedControlsForChecklistItemResults,
} from 'store/assessments/selectors';
import { cloneDeep, isEqual } from 'lodash';
import { filterArrayEmpty } from 'lib/riskbase';
import { pluralise } from 'lib/pluralise';
import {
  AssessmentQuestionType,
  GenericKeyAseShowSuggestionsUnderChecklistItems,
} from 'types/types';
import {
  ChecklistItemHasSuggestedActions,
  ChecklistItemHasSuggestedControls,
  ChecklistItemResultEnum,
  QuestionResult,
} from 'store/assessments/types';
import { ScreenSize } from 'lib/constants';
import { TextAreaField } from 'components/Field/TextAreaField/TextAreaField';
import { NAVIGATE_MODAL_BACK, SET_GENERIC } from 'store/actions';
import { useConfirmUnsaved } from 'hooks/useConfirm';
import { useForceUpdate } from 'hooks/useForceUpdate';
import { useEsc } from 'hooks/useEsc';
import { ChecklistItemRow } from '../Parts/ChecklistItemRow';
import { PageHeader } from 'components/PageHeader';
import { View } from 'react-native';
import { GapSmall } from 'components/GapSmall';

export const QuestionEdit: React.FC<
  | ModalScreenProps<ModalScreens.AseQuestion>
  | ScreenProps<Stacks.ASSESSMENT, Screens.ASE_QUESTION>
  | ScreenProps<Stacks.ASEREPEATABLE, Screens.ASEREPEATABLE_QUESTION>
> = ({ navigation, route, isModal }) => {
  const dispatch = useDispatch();
  const screenSize = useScreenSize();
  const confirmUnsaved = useConfirmUnsaved();
  const forceUpdate = useForceUpdate();
  const { id, fieldInstanceId, parent } = route.params;

  const templateSettings = useSelector(selectAseTemplateSettings);

  const question = useSelector((state) => selectAseTplQuestion(state, id));

  const suggestionsUnderChecklistItems =
    useSelector(selectAseShowSuggestionsUnderChecklistItems) &&
    question.type === AssessmentQuestionType.checklist;

  const answers = useSelector((state) =>
    selectAseTplQuestionAnswers(state, id),
  );
  const checklistItems = useSelector((state) =>
    selectAseTplChecklistItemsForQuestion(state, id),
  );

  const fieldInstance = useSelector((state) =>
    selectAseFieldInstanceAse(state, fieldInstanceId || '-1'),
  );

  const questionResult = useSelector((state) =>
    selectAseResultQuestion(state, id, fieldInstanceId),
  );
  const answerId = useSelector((state) =>
    selectAseResultQuestionAnswerId(state, id, fieldInstanceId),
  );
  const checklistItemResults = useSelector((state) =>
    selectAseResultChecklistItemsForQuestion(state, id, fieldInstanceId),
  );
  const comment =
    useSelector((state) =>
      selectAseQuestionComment(state, id, fieldInstanceId),
    ) || '';

  const previousAnswerId = useSelector((state) =>
    selectAseQuestionPreviousAnswer(state, id, fieldInstanceId),
  );

  const isInParentAstAse = useSelector((state) =>
    selectAseFieldInstanceChildInstanceIsInParentAstAse(
      state,
      parent,
      fieldInstanceId || '-1',
    ),
  );

  const dataRef = React.useRef<{
    answerId: QuestionResult['answerId'];
    checklistItemResults: Record<string, ChecklistItemResultEnum | null>;
    comment: string;
  }>(
    cloneDeep({
      answerId: answerId || '',
      checklistItemResults,
      comment,
    }),
  );

  const initDataRef = React.useRef<{
    answerId: QuestionResult['answerId'];
    checklistItemResults: Record<string, ChecklistItemResultEnum | null>;
    comment: string;
  }>(cloneDeep(dataRef.current));

  const internalSave = !!isModal;

  if (!internalSave) {
    dataRef.current = cloneDeep({
      answerId: answerId || '',
      checklistItemResults,
      comment,
    });
    initDataRef.current = dataRef.current;
  }

  const isDirtyRef = React.useRef(false);
  const checkIsDirty = React.useCallback(
    () => !isEqual(initDataRef.current, dataRef.current),
    [],
  );
  isDirtyRef.current = checkIsDirty();

  const hasResponse =
    question.type === AssessmentQuestionType.question
      ? !!dataRef.current.answerId
      : !!Object.values(dataRef.current.checklistItemResults).filter(
          (v) => v !== null,
        ).length;

  const hasResponseNotYetSaved =
    hasResponse &&
    (question.type === AssessmentQuestionType.question
      ? !initDataRef.current.answerId
      : !Object.values(initDataRef.current.checklistItemResults).filter(
          (v) => v !== null,
        ).length);

  const suggestedControls = useSelector((state) =>
    question.type === AssessmentQuestionType.question
      ? selectAseSuggestedControlsForQuestionId(state, id)
      : selectAseSuggestedControlsForChecklistItemResults(
          state,
          dataRef.current.checklistItemResults,
        ),
  );

  const suggestedActions = useSelector((state) =>
    question.type === AssessmentQuestionType.question
      ? selectAseSuggestedActionsForAnswerId(state, dataRef.current.answerId)
      : selectAseSuggestedActionsForChecklistItemResults(
          state,
          dataRef.current.checklistItemResults,
        ),
  );

  const suggestedActionsByChecklistItem = React.useMemo(
    () =>
      suggestedActions.reduce<ChecklistItemHasSuggestedActions>(
        (acc, suggestion) => {
          suggestion.positiveChecklistItemIds?.forEach((checklistItemId) => {
            acc[checklistItemId] = acc[checklistItemId] ?? {
              positive: [],
              negative: [],
              na: [],
            };
            acc[checklistItemId]?.positive.push(suggestion);
          });
          suggestion.negativeChecklistItemIds?.forEach((checklistItemId) => {
            acc[checklistItemId] = acc[checklistItemId] ?? {
              positive: [],
              negative: [],
              na: [],
            };
            acc[checklistItemId]?.negative.push(suggestion);
          });
          return acc;
        },
        {},
      ),
    [suggestedActions],
  );

  const suggestedControlsByChecklistItem = React.useMemo(
    () =>
      suggestedControls.reduce<ChecklistItemHasSuggestedControls>(
        (acc, suggestion) => {
          suggestion.positiveChecklistItemIds?.forEach((checklistItemId) => {
            acc[checklistItemId] = acc[checklistItemId] ?? {
              positive: [],
              negative: [],
              na: [],
            };
            acc[checklistItemId]?.positive.push(suggestion);
          });
          suggestion.negativeChecklistItemIds?.forEach((checklistItemId) => {
            acc[checklistItemId] = acc[checklistItemId] ?? {
              positive: [],
              negative: [],
              na: [],
            };
            acc[checklistItemId]?.negative.push(suggestion);
          });
          return acc;
        },
        {},
      ),
    [suggestedControls],
  );

  const actionsCount = useSelector(
    (state) => selectAseQuestionActionIds(state, id),
    isEqual,
  ).length;
  const controlsCount = useSelector(
    (state) => selectAseQuestionControlIds(state, id),
    isEqual,
  ).length;

  const findingExplanation =
    (!!actionsCount || !!controlsCount) &&
    `${[
      pluralise(actionsCount, '1 action', '{x} actions', ''),
      pluralise(controlsCount, '1 control', '{x} controls', ''),
    ]
      .filter(filterArrayEmpty)
      .join(' and ')} added to ${
      question.type === AssessmentQuestionType.checklist
        ? 'checklist'
        : 'question'
    }`;

  const handleSetAnswer = React.useCallback(
    (newAnswerId: string) => {
      if (dataRef.current.answerId !== newAnswerId) {
        if (internalSave) {
          dataRef.current = { ...dataRef.current, answerId: newAnswerId };
        } else {
          dispatch(
            doAseUpdateQuestion({
              questionId: id,
              fieldInstanceId,
              answerId: newAnswerId,
            }),
          );
        }
      }
      forceUpdate();
    },
    [dispatch, fieldInstanceId, forceUpdate, id, internalSave],
  );

  const handleSaveChecklistItemResult = React.useCallback(
    (checklistItemId: number, newResult: ChecklistItemResultEnum | null) => {
      if (dataRef.current.checklistItemResults[checklistItemId] !== newResult) {
        if (internalSave) {
          dataRef.current = {
            ...dataRef.current,
            checklistItemResults: {
              ...dataRef.current.checklistItemResults,
              [checklistItemId]: newResult,
            },
          };
        } else {
          dispatch(
            doAseUpdateQuestion({
              questionId: id,
              fieldInstanceId,
              checklistItemResults: { [checklistItemId]: newResult },
            }),
          );
        }
      }
      forceUpdate();
    },
    [dispatch, fieldInstanceId, forceUpdate, id, internalSave],
  );

  const saveComment = React.useCallback(
    (newComment: string) => {
      if (!isEqual(dataRef.current.comment, newComment)) {
        if (internalSave) {
          dataRef.current = { ...dataRef.current, comment: newComment };
          if (checkIsDirty() !== isDirtyRef.current) {
            forceUpdate();
          }
        } else {
          dispatch(
            doAseUpdateQuestion({
              questionId: id,
              fieldInstanceId,
              comment: newComment,
            }),
          );
        }
      }
    },
    [checkIsDirty, dispatch, fieldInstanceId, forceUpdate, id, internalSave],
  );

  // Blank page if page is invalid
  const isBeingOpenInvalid =
    fieldInstanceId && (!fieldInstance || (parent && !isInParentAstAse));
  React.useEffect(() => {
    if (isBeingOpenInvalid) {
      if (isModal) {
        dispatch(NAVIGATE_MODAL_BACK([ModalScreens.AseQuestion]));
      } else if (screenSize === ScreenSize.small) {
        navigation?.goBack();
      }
    }
  }, [dispatch, isBeingOpenInvalid, isModal, navigation, screenSize]);

  const handleHardSave = React.useCallback(() => {
    if (checkIsDirty()) {
      dispatch(
        doAseUpdateQuestion({
          questionId: id,
          fieldInstanceId,
          answerId: dataRef.current.answerId,
          checklistItemResults: dataRef.current.checklistItemResults || {},
          comment: dataRef.current.comment,
        }),
      );
    } else if (isModal) {
      dispatch(NAVIGATE_MODAL_BACK([ModalScreens.AseQuestion]));
    } else if (screenSize === 'small') {
      navigation?.goBack();
    }
  }, [
    checkIsDirty,
    dispatch,
    fieldInstanceId,
    id,
    isModal,
    navigation,
    screenSize,
  ]);

  const handleDeleteAll = () => {
    if (question.type === AssessmentQuestionType.checklist) {
      dispatch(
        doAseUpdateQuestion({
          questionId: id,
          fieldInstanceId,
          checklistItemResults: checklistItems.reduce<
            Record<string, ChecklistItemResultEnum | null>
          >((acc, checklistItem) => {
            acc[checklistItem.id] = null;
            return acc;
          }, {}),
        }),
      );
    } else {
      dispatch(
        doAseDeleteQuestionResult({
          questionId: id,
          fieldInstanceId,
        }),
      );
    }
  };

  const handleClose = () => {
    if (isModal) {
      if (checkIsDirty()) {
        confirmUnsaved({
          onSave: handleHardSave,
          onDiscard: () =>
            dispatch(NAVIGATE_MODAL_BACK([ModalScreens.AseQuestion])),
        });
        return;
      }
      dispatch(NAVIGATE_MODAL_BACK([ModalScreens.AseQuestion]));
    } else if (screenSize === 'small') {
      navigation?.goBack();
    }
  };

  useEsc(isModal && handleClose);

  if (isBeingOpenInvalid) {
    return <Page />;
  }

  const showAddActionSection =
    hasResponse &&
    !hasResponseNotYetSaved &&
    (templateSettings.addCustomActions ||
      (!suggestionsUnderChecklistItems && !!suggestedActions.length));
  const showAddControlSection =
    hasResponse &&
    !hasResponseNotYetSaved &&
    (templateSettings.addCustomControls ||
      (!suggestionsUnderChecklistItems && !!suggestedControls.length));

  return (
    <Page
      scrollView
      header={
        <PageHeader
          title={
            question.type === AssessmentQuestionType.question
              ? 'Question'
              : 'Checklist'
          }
          onPressBackButton={
            !isModal && screenSize === 'small' ? navigation?.goBack : undefined
          }
          onPressCancelButton={isModal ? handleClose : undefined}
          onPressSaveButton={isModal ? handleHardSave : undefined}
          menuRight={
            question.type === AssessmentQuestionType.checklist
              ? {
                  iconButtonConfig: { icon: ICONS.EllipsisLight },
                  options: [
                    {
                      text: 'Show Suggestions Inline',
                      subText:
                        'Action & Control Suggestions Under Checklist Items',
                      leftIcon: suggestionsUnderChecklistItems
                        ? ICONS.SquareCheckSolid
                        : ICONS.SquareLight,
                      onPress: () =>
                        dispatch(
                          SET_GENERIC({
                            key: GenericKeyAseShowSuggestionsUnderChecklistItems,
                            value: !suggestionsUnderChecklistItems,
                          }),
                        ),
                    },
                    '-',
                    {
                      text: 'Delete Checklist',
                      icon: ICONS.TrashCanLight,
                      onPress: handleDeleteAll,
                      destructive: true,
                    },
                  ],
                }
              : questionResult?.answerId
                ? {
                    iconButtonConfig: { icon: ICONS.EllipsisLight },
                    options: [
                      {
                        text: 'Delete Answer',
                        icon: ICONS.TrashCanLight,
                        onPress: handleDeleteAll,
                        destructive: true,
                      },
                    ],
                  }
                : undefined
          }
        />
      }
    >
      <CardGeneric>
        <Txt weight="bold" size="large">
          {question.question}
        </Txt>
        {!!question.description && (
          <Txt style={{ color: theme.colors.fieldValueText }}>
            {question.description}
          </Txt>
        )}
      </CardGeneric>

      {!!checklistItems.length && (
        <GapSmall>
          {checklistItems.map((checklistItem, index, array) => (
            <Card
              key={checklistItem.id}
              explanation={index === array.length - 1 ? findingExplanation : ''}
            >
              <ChecklistItemRow
                checklistItem={checklistItem}
                canAddActions
                canAddControls
                checklistItemResults={dataRef.current.checklistItemResults}
                fieldInstanceId={fieldInstanceId}
                questionId={id}
                parent={parent}
                handleSaveChecklistItemResult={handleSaveChecklistItemResult}
                suggestedActionsByChecklistItem={
                  suggestedActionsByChecklistItem
                }
                suggestedControlsByChecklistItem={
                  suggestedControlsByChecklistItem
                }
                suggestionsUnderChecklistItems={suggestionsUnderChecklistItems}
              />
            </Card>
          ))}
        </GapSmall>
      )}

      {!!answers.length && (
        <Card explanation={findingExplanation}>
          {answers.map((tplAnswer) => (
            <CardRow
              key={tplAnswer.id}
              radioValue={tplAnswer.id === dataRef.current.answerId}
              onPress={() => handleSetAnswer(tplAnswer.id)}
            >
              <Txt>
                <Txt style={tplAnswer.negative && { color: theme.colors.red }}>
                  {tplAnswer.answer}
                </Txt>
                {tplAnswer.description && (
                  <Txt style={{ color: theme.colors.fieldValueText }}>
                    &nbsp;&nbsp;{tplAnswer.description}
                  </Txt>
                )}
              </Txt>
              {previousAnswerId === tplAnswer.id && (
                <Txt
                  size="small"
                  style={{
                    color: theme.colors.purple,
                    marginTop: theme.grid / 2,
                  }}
                >
                  Chosen the last time this question was answered
                </Txt>
              )}
            </CardRow>
          ))}
        </Card>
      )}

      {hasResponse &&
        !hasResponseNotYetSaved &&
        (templateSettings.questionComments || !!dataRef.current.comment) && (
          <Card label="Remarks">
            <TextAreaField
              placeholder="No Remarks"
              value={dataRef.current.comment}
              onChange={saveComment}
              onEsc={handleClose}
            />
          </Card>
        )}

      {(showAddActionSection || showAddControlSection) && (
        <View>
          <Card
            label={`${
              suggestionsUnderChecklistItems ? 'Add Custom ' : 'Add '
            }${[
              showAddActionSection ? 'Actions' : null,
              showAddControlSection ? 'Controls' : null,
            ]
              .filter(filterArrayEmpty)
              .join(' & ')}`}
          />
          <GapSmall>
            {showAddActionSection && (
              <Card>
                {!suggestionsUnderChecklistItems &&
                  suggestedActions.map((action) => (
                    <AddTemplateAction
                      key={action.id}
                      parentId={id}
                      parentType="question"
                      templateId={action.id}
                      fieldInstanceId={fieldInstanceId}
                      parent={parent}
                    />
                  ))}
                {templateSettings.addCustomActions && (
                  <AddCustomAction
                    parentId={id}
                    parentType="question"
                    fieldInstanceId={fieldInstanceId}
                    parent={parent}
                  />
                )}
              </Card>
            )}
            {showAddControlSection && (
              <Card>
                {!suggestionsUnderChecklistItems &&
                  suggestedControls.map((control) => (
                    <AddTemplateControl
                      key={control.id}
                      parentId={id}
                      parentType="question"
                      templateId={control.id}
                      fieldInstanceId={fieldInstanceId}
                      parent={parent}
                    />
                  ))}
                {templateSettings.addCustomControls && (
                  <AddCustomControl
                    parentId={id}
                    parentType="question"
                    fieldInstanceId={fieldInstanceId}
                    parent={parent}
                  />
                )}
              </Card>
            )}
          </GapSmall>
        </View>
      )}
    </Page>
  );
};
